import { DoubleKeyRecord } from '@/src/utils/doubleKeyRecord';
import { StateFile, ViewConfig } from '../../io/state-file/schema';
import { ensureDefault } from '../../utils';

type ViewConfigStateKey = keyof ViewConfig;

const serializeViewConfig = <
  K extends ViewConfigStateKey,
  V extends ViewConfig[K],
>(
  stateFile: StateFile,
  viewConfigs: DoubleKeyRecord<V>,
  viewConfigStateKey: K
) => {
  const datasets = stateFile.manifest.datasets;
  const viewByID = stateFile.manifest.viewByID;
  if (!datasets || !viewByID) return;

  const dataIDs = datasets.map((dataset) => dataset.id);
  const views = Object.values(viewByID);

  views.forEach((view) => {
    const savedConfigs = view.config ?? {};
    Object.entries(savedConfigs).forEach(([dataID, config]) => {
      delete config[viewConfigStateKey];
      if (!Object.keys(config).length) delete savedConfigs[dataID];
    });
    dataIDs.forEach((dataID) => {
      const viewConfig = viewConfigs[view.id]?.[dataID];
      if (viewConfig !== undefined) {
        // Initialize config if it doesn't exist
        if (!view.config) {
          view.config = {};
        }
        const configForData = ensureDefault(
          dataID,
          view.config,
          {} as ViewConfig
        );
        configForData[viewConfigStateKey] = viewConfig as ViewConfig[K];
      }
    });
  });
};

/**
 * @param viewConfigs Expected to be a DoubleKeyRecord. Index is ordered as (ViewID, DataID)
 * @param viewConfigStateKey
 * @returns
 */
export const createViewConfigSerializer = <
  K extends ViewConfigStateKey,
  V extends ViewConfig[K],
>(
  viewConfigs: DoubleKeyRecord<V>,
  viewConfigStateKey: K
) => {
  return (stateFile: StateFile) => {
    serializeViewConfig(stateFile, viewConfigs, viewConfigStateKey);
  };
};
